import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

function MdRenderer({ mdString, modalContent, showMoreButton = true }) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Words to highlight (case-insensitive)
  const highlightWords = [
    'error',
    'warning',
    'success',
    'growth',
    'growing',
    'executive',
    'summary',
  ];

  // Color map (used as inline style — or switch to CSS classes below)
  const colorMap = {
    error: 'red',
    warning: 'orange',
    success: 'green',
    brand: '#B22234',
    growth: 'green',
    growing: 'green',
    executive: '#04265C',
    summary: '#B22234',
  };

  /**
   * Injects <span> wrappers into Markdown strings so ReactMarkdown (with rehypeRaw)
   * will render the highlights. We keep brand* as a special case.
   * Note: We are deliberately allowing raw HTML via rehypeRaw below.
   * If your Markdown source can be user-submitted/unsafe, consider a safer AST plugin approach.
   */
  const highlightMarkdown = (md) => {
    if (!md) return '';

    let highlighted = md;

    // Highlight specific words
    highlightWords.forEach((word) => {
      const regex = new RegExp(`\\b(${word})\\b`, 'gi');
      highlighted = highlighted.replace(
        regex,
        (_m, g1) =>
          `<span style="color:${colorMap[word.toLowerCase()]};font-weight:bold;">${g1}</span>`
      );
    });

    // Special: words starting with "brand" (brand, branding, branded, etc.)
    const brandRegex = /\b(brand\w*)\b/gi;
    highlighted = highlighted.replace(
      brandRegex,
      (_m, g1) =>
        `<span style="color:${colorMap.brand};font-weight:bold;">${g1}</span>`
    );

    return highlighted;
  };

  const highlightedMain = highlightMarkdown(mdString);
  const highlightedModal = highlightMarkdown(modalContent);

  return (
    <>
      {/* Main Markdown rendering */}
      <div style={{ paddingLeft: '30px' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {highlightedMain}
        </ReactMarkdown>
      </div>

      {/* More button */}
      {showMoreButton && (
        <Button
          onClick={handleOpen}
          variant="contained"
          startIcon={<ReadMoreIcon />}
          sx={{
            mt: 2,
            ml: '30px',
            textTransform: 'none',
            backgroundColor: '#fafbfc',
            color: '#00265E',
            fontWeight: 'bold',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            px: 2,
            py: 1,
            '&:hover': {
              color: '#ffffff',
              backgroundColor: '#001f4d',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          More
        </Button>
      )}

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            maxWidth: 1100,
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            p: 4,
          }}
        >
          {/* Scrollable Markdown content */}
          <div style={{ overflowY: 'auto' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {highlightedModal}
            </ReactMarkdown>
          </div>

          <Box mt={3} textAlign="right">
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{
                mt: 2,
                textTransform: 'none',
                backgroundColor: '#00265E',
                color: '#fafbfc',
                fontWeight: 'bold',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                px: 2,
                py: 1,
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: '#001433',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

MdRenderer.propTypes = {
  mdString: PropTypes.string.isRequired,   // Markdown for main view
  modalContent: PropTypes.string.isRequired, // Markdown for modal view
  showMoreButton: PropTypes.bool,
};

export default MdRenderer;
